import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseISO, eachDayOfInterval, format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useTranslation as useI18n } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Button } from '@/shared/components/ui/button';
import { PerformanceTimelineRow } from './PerformanceTimelineRow';
import { PerformanceLegend } from './PerformanceLegend';
import type { Locale } from 'date-fns';
import type { MemberTimelineEntry, DayColumn } from '../types';

interface PerformanceTimelineProps {
  entries: MemberTimelineEntry[];
  periodStart: string;
  periodEnd: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  showLegend?: boolean;
}

function useDateLocale() {
  const { i18n } = useI18n();
  return i18n.language.startsWith('es') ? es : enUS;
}

function buildDayColumns(periodStart: string, periodEnd: string, locale: Locale): DayColumn[] {
  const days = eachDayOfInterval({ start: parseISO(periodStart), end: parseISO(periodEnd) });
  return days.map((d) => ({
    date: format(d, 'yyyy-MM-dd'),
    dayNumber: d.getDate(),
    dayLabel: format(d, 'EEE', { locale }),
  }));
}

export function PerformanceTimeline({
  entries,
  periodStart,
  periodEnd,
  isLoading = false,
  isError = false,
  onRetry,
  showLegend = true,
}: PerformanceTimelineProps) {
  const { t } = useTranslation('laborPerformance');
  const locale = useDateLocale();
  const days = useMemo(
    () => buildDayColumns(periodStart, periodEnd, locale),
    [periodStart, periodEnd, locale],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid="labor-performance-timeline-error"
        className="rounded-lg border border-red-200 bg-red-50 p-4"
      >
        <p className="text-sm font-medium text-red-800">{t('states.error')}</p>
        {onRetry && (
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
            {t('states.retry')}
          </Button>
        )}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p
        data-testid="labor-performance-timeline-empty"
        className="py-8 text-center text-sm text-muted-foreground"
      >
        {t('states.empty')}
      </p>
    );
  }

  return (
    <div data-testid="labor-performance-timeline-table" className="flex flex-col">
      <div className="overflow-x-auto rounded-lg border">
        <div className="min-w-max">
          {/* Header */}
          <div
            data-testid="labor-performance-timeline-header"
            className="flex items-center border-b bg-muted/30"
          >
            <div className="w-48 flex-shrink-0 py-2 pl-4 pr-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:w-56">
              {t('timeline.collaboratorCol')}
            </div>
            <div className="flex flex-1">
              {days.map((day) => (
                <div
                  key={day.date}
                  className="flex flex-1 flex-col items-center justify-center border-l py-2 px-1"
                >
                  <span className="text-xs font-semibold text-foreground">{day.dayNumber}</span>
                  <span className="text-[10px] capitalize text-muted-foreground">
                    {day.dayLabel}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {entries.map((entry) => (
            <PerformanceTimelineRow key={entry.memberId} entry={entry} days={days} />
          ))}
        </div>
      </div>

      {showLegend && <PerformanceLegend className="mt-3 px-4" />}
    </div>
  );
}
